import { useDeviceStore } from '@/store';
import { DeviceLogger } from './logger';
import logger from '@/lib/logger';
import {
  encodeAppMessage,
  frameBytes,
  tryDecodeDevMessageWithCrcStatus,
  createAppMessage,
  hasError,
  getErrorMessage,
  isRecoverableError,
  validateAcknowledgement,
  getFirmwareVersion,
  getHardwareVersion,
  getProtocolVersion,
  Triggers,
  ElectrodeDetect,
  PowerState,
} from '@/lib/protobuf';
import type { ImpedanceExec, DevMessage, ImpedanceResult, AppMessage } from '@/lib/protobuf';
import type { DeviceInfo, DeviceState, MeasurementResult } from '@/types/device';
import type { DeviceServiceOptions, MeasurementOptions, ConnectResult } from './types';
import { CRC_FALLBACK, DEVICE_STORAGE, EDIPHOR_USB, TIMING } from './constants';
import { createDeviceError, classifyError, DeviceErrorCode } from './errors';
import type { IDeviceService, TransferIncident } from './IDeviceService';
import { FrameAssembler } from './FrameAssembler';
import { WebUsbTransport, type UsbTransport } from './UsbTransport';

/**
 * DeviceService - Handles WebUSB communication with Ediphor measurement device
 *
 * This is a singleton service that manages the USB connection lifecycle,
 * sends protobuf-encoded commands, and receives/decodes device responses.
 */
export class DeviceService implements IDeviceService {
  private static instance: DeviceService | null = null;

  private device: USBDevice | null = null;
  // Data-plane seam (AD-5). `_transportOverride` is injected (tests); otherwise a
  // WebUsbTransport is derived from `this.device` and cached per device.
  private _transportOverride: UsbTransport | null = null;
  private _webTransport: { device: USBDevice; transport: UsbTransport } | null = null;
  private inEndpoint: number = 0;
  private outEndpoint: number = 0;
  private logger: DeviceLogger;
  private options: {
    vendorId: number;
    productIds: number[];
    autoReconnect: boolean;
    timeouts: {
      connection: number;
      command: number;
      measurement: number;
    };
  };
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;

  // Prevents concurrent tryAutoReconnect() calls from racing on this.device
  private _autoReconnectInProgress = false;

  // Set to true when beforeunload fires — stops handleDisconnect from mutating
  // store state (which would trigger React re-renders and error dialogs) when
  // the browser is tearing down the page and USB connections along with it.
  private _isPageUnloading = false;

  // Background receive loop
  private _bgLoopAbort: AbortController | null = null;
  /** Reassembles frames split/coalesced across transferIn results (AD-4). */
  private _frameAssembler = new FrameAssembler((reason, droppedBytes) => {
    this.logger.warn('USB RX', `Bad frame data discarded: ${reason} (${droppedBytes} byte(s))`);
  });
  /** Raw bytes of the most-recently resolved command response.  Cleared on the
   *  next send so reading it immediately after a command gives the exact frame. */
  private _lastCmdRaw: Uint8Array | null = null;
  private _rxResolvers: Array<{
    resolve: (msg: DevMessage | null) => void;
    reject: (err: unknown) => void;
    timer: ReturnType<typeof setTimeout>;
    expectedId: number;
  }> = [];
  /**
   * Tracks which Triggers was sent for a given correlation id (AppMessage.id), so
   * `_receiveLoop` can gate the "recoverable partial result" path to only the
   * triggers that can legitimately carry a partial impedanceResult
   * (MEASURE_ABORT / MEASURE_RESULT) — not BLINK/SLEEP/REBOOT, which never do.
   * Kept out-of-band from `receiveResponse`'s resolver bookkeeping so that
   * method's signature (and its many direct callers in tests) don't change.
   */
  private _pendingTriggerByCorrelationId: Map<number, Triggers> = new Map();
  private _unsolicitedHandlers: ((msg: DevMessage, raw: Uint8Array) => void)[] = [];
  private _sentHandlers: ((raw: Uint8Array, message: AppMessage) => void)[] = [];
  private _receivedHandlers: ((raw: Uint8Array, msg: DevMessage, crcValid: boolean) => void)[] = [];
  private _transferIncidentHandlers: ((incident: TransferIncident) => void)[] = [];

  // transferIn failure-episode tracking for the background receive loop — see
  // _handleTransferError / _handleTransferSuccess.
  private _consecutiveTransferErrors = 0;
  private _transferIncidentStartedAt: number | null = null;

  /** Count of inbound frames whose CRC32 failed but were still decoded and used
   *  (CRC_FALLBACK.ALLOW_DECODE_ON_MISMATCH), for runtime diagnostics. */
  private _crcFallbackUsedCount = 0;

  /** Active data-plane transport, or null when not connected. */
  private get transport(): UsbTransport | null {
    if (this._transportOverride) return this._transportOverride;
    if (!this.device) return null;
    if (this._webTransport?.device !== this.device) {
      this._webTransport = { device: this.device, transport: new WebUsbTransport(this.device) };
    }
    return this._webTransport.transport;
  }

  /**
   * Inject a transport (tests) that replaces the one derived from `this.device`.
   * Pass null to revert. The override is also cleared whenever the device is dropped.
   */
  public attachTransport(transport: UsbTransport | null): void {
    this._transportOverride = transport;
  }

  /**
   * Get the singleton instance of DeviceService
   */
  public static getInstance(options?: DeviceServiceOptions): DeviceService {
    if (!DeviceService.instance) {
      DeviceService.instance = new DeviceService(options);
    }
    return DeviceService.instance;
  }

  /**
   * Reset the singleton instance (for testing)
   */
  public static resetInstance(): void {
    if (DeviceService.instance) {
      DeviceService.instance.disconnect();
      DeviceService.instance = null;
    }
  }

  private constructor(options?: DeviceServiceOptions) {
    this.logger = DeviceLogger.setup();

    this.options = {
      vendorId: options?.vendorId ?? EDIPHOR_USB.VENDOR_ID,
      productIds: options?.productIds ?? [...EDIPHOR_USB.PRODUCT_IDS],
      autoReconnect: options?.autoReconnect ?? true,
      timeouts: {
        connection: options?.timeouts?.connection ?? TIMING.CONNECTION_TIMEOUT,
        command: options?.timeouts?.command ?? TIMING.COMMAND_TIMEOUT,
        measurement:
          options?.timeouts?.measurement ?? TIMING.MAX_MEASUREMENT_DURATION,
      },
    };

    this.logger.info('DeviceService', 'DeviceService initialized', this.options);

    // Listen for USB connect/disconnect events
    if (this.isWebUSBSupported()) {
      navigator.usb.addEventListener('disconnect', this.handleDisconnect);
      navigator.usb.addEventListener('connect', this.handleConnect);
    }

    // Mark the page as unloading on beforeunload so that handleDisconnect
    // (which fires when the browser tears down USB connections mid-unload)
    // does not trigger store updates or reconnect timers.
    if (typeof window !== 'undefined') {
      window.addEventListener('beforeunload', this._onBeforeUnload);
    }
  }

  /**
   * Check if WebUSB is supported in current browser
   */
  public isWebUSBSupported(): boolean {
    // simple existence check - if the property is present but undefined we
    // should treat it as unsupported (previous implementation would return
    // true because `'usb' in navigator` is true even when navigator.usb ===
    // undefined).
    return (
      typeof navigator !== 'undefined' &&
      typeof (navigator as any).usb !== 'undefined' &&
      (navigator as any).usb !== null
    );
  }

  /**
   * Check if Web Bluetooth is supported in current browser.
   *
   * DeviceService is the WebUSB transport; this check exists purely so
   * `DeviceService` satisfies `IDeviceService` (which every transport
   * implementation must, per the interface's new BLE capability check) — it
   * does not affect USB connection behavior at all.
   */
  public isBleSupported(): boolean {
    return (
      typeof navigator !== 'undefined' &&
      typeof (navigator as any).bluetooth !== 'undefined' &&
      (navigator as any).bluetooth !== null
    );
  }

  /**
   * Open a USB device, claim its interface, start the receive loop, and query
   * initial device state.  Shared by connect() and tryAutoReconnect() to keep
   * the two paths in sync.
   *
   * Precondition: this.device has already been set to the target USBDevice.
   * On success the store is updated (setDeviceInfo + setConnectionState('connected')).
   * On failure this.device is nulled and the exception propagates.
   */
  private async _initialiseOpenDevice(): Promise<DeviceInfo> {
    const device = this.device!;
    const transport = this.transport!;

    await device.open();
    this.log('Device opened');

    if (!device.configuration) {
      await device.selectConfiguration(1);
      this.log('Configuration selected');
    } else {
      this.log('Configuration already set:', device.configuration.configurationValue);
    }

    const iface = device.configuration!.interfaces[0];
    this.inEndpoint = iface.alternate.endpoints[0].endpointNumber;
    this.outEndpoint = iface.alternate.endpoints[1].endpointNumber;
    this.log('Endpoints:', { in: this.inEndpoint, out: this.outEndpoint });

    await transport.claimInterface(EDIPHOR_USB.INTERFACE_NUMBER);
    this.log('Interface claimed');

    // Start the background receive loop — owns ALL transferIn calls from this point
    this._startReceiveLoop();

    // Settle delay — device needs a moment after claimInterface
    await new Promise((r) => setTimeout(r, 150));

    let deviceInfo: DeviceInfo = {
      uid: '00:00:00:00:00:00',
      uidBytes: new Uint8Array(6),
      firmwareVersion: '0',
      hardwareVersion: '0',
      serialNumber: '0',
    };

    try {
      const devMsg = await this.sendCommand(Triggers.NONE);
      if (devMsg) {
        this.updateDeviceState(devMsg);
        const uid = Array.from(devMsg.uid ?? [])
          .map((b) => b.toString(16).padStart(2, '0'))
          .join(':');
        deviceInfo = {
          uid: devMsg.uid?.some((b) => b !== 0) ? uid : '00:00:00:00:00:00',
          uidBytes: devMsg.uid ?? new Uint8Array(6),
          firmwareVersion: getFirmwareVersion(devMsg.version),
          hardwareVersion: getHardwareVersion(devMsg.version),
          protocolVersion: getProtocolVersion(devMsg.version),
          serialNumber: devMsg.serialNumber?.toString() ?? '0',
        };
        this.logger.info('DeviceService', 'Post-connect query succeeded', deviceInfo);
      }
    } catch (e) {
      this.logger.warn('DeviceService', 'Post-connect state query failed (non-fatal)', e);
    }

    const store = useDeviceStore.getState();
    store.setDeviceInfo(deviceInfo);
    store.setConnectionState('connected');
    store.clearError();
    localStorage.setItem(DEVICE_STORAGE.PAIRED_KEY, '1');

    return deviceInfo;
  }

  /**
   * Request user to select and connect to a device
   */
  public async connect(): Promise<ConnectResult> {
    if (!this.isWebUSBSupported()) {
      const error = createDeviceError(DeviceErrorCode.WEBUSB_NOT_SUPPORTED);
      useDeviceStore.getState().setError(error);
      return { success: false, error: new Error(error.message) };
    }

    const store = useDeviceStore.getState();
    store.setConnectionState('connecting');

    try {
      this.device = await navigator.usb.requestDevice({
        filters: this.options.productIds.map((productId) => ({
          vendorId: this.options.vendorId,
          productId,
        })),
      });

      const deviceInfo = await this._initialiseOpenDevice();
      this.log('Connected to device');

      return { success: true, deviceInfo: { ...deviceInfo, serialNumber: parseInt(deviceInfo.serialNumber) || 0 } };
    } catch (error) {
      this.log('Connection failed:', error);
      const deviceError = classifyError(error);
      store.setConnectionState('disconnected');
      store.setError(deviceError);
      this.device = null;
      this._transportOverride = null;
      return { success: false, error: error as Error };
    }
  }

  /**
   * Disconnect from the current device
   */
  public async disconnect(): Promise<void> {
    this._stopReceiveLoop();

    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    // Null this.device BEFORE the first await so that when the JS event loop
    // yields at releaseInterface(), any in-flight transferIn microtask will
    // see this.device === null and exit the receive loop immediately.
    const device = this.device;
    const transport = this.transport;
    this.device = null;
    this._transportOverride = null;

    if (device && transport) {
      try {
        await transport.releaseInterface(EDIPHOR_USB.INTERFACE_NUMBER);
        await device.close();
      } catch {
        // Ignore errors during disconnect
      }
    }

    const store = useDeviceStore.getState();
    localStorage.removeItem(DEVICE_STORAGE.PAIRED_KEY);
    store.reset();
    this.log('Disconnected');
  }

  /**
   * Trigger device LED blink for identification
   */
  public async blink(): Promise<DevMessage | null> {
    try {
      return await this.sendCommand(Triggers.BLINK);
    } catch {
      return null;
    }
  }

  /**
   * Get current device state
   */
  public async getState(): Promise<DevMessage | null> {
    return this.sendCommand(Triggers.NONE);
  }

  /**
   * Send a custom command (for diagnostic/testing purposes)
   */
  public async sendCustomCommand(
    trigger: Triggers,
    impedanceExec?: ImpedanceExec
  ): Promise<DevMessage | null> {
    return this.sendCommand(trigger, impedanceExec);
  }

  /**
   * Subscribe to unsolicited (watchdog) messages from the device.
   * Returns an unsubscribe function.
   */
  public onUnsolicitedMessage(
    handler: (msg: DevMessage, raw: Uint8Array) => void
  ): () => void {
    this._unsolicitedHandlers.push(handler);
    return () => {
      this._unsolicitedHandlers = this._unsolicitedHandlers.filter((h) => h !== handler);
    };
  }

  /**
   * Subscribe to raw frames transmitted to the device (harness communication logging).
   * Returns an unsubscribe function.
   */
  public onSentMessage(handler: (raw: Uint8Array, message: AppMessage) => void): () => void {
    this._sentHandlers.push(handler);
    return () => {
      this._sentHandlers = this._sentHandlers.filter((h) => h !== handler);
    };
  }

  /**
   * Notify onSentMessage subscribers of a frame that was just transmitted successfully.
   */
  private notifySent(raw: Uint8Array, message: AppMessage): void {
    for (const handler of this._sentHandlers) {
      try {
        handler(raw, message);
      } catch (e) {
        this.logger.warn('USB TX', 'Sent-message handler threw', e);
      }
    }
  }

  /**
   * Subscribe to EVERY raw frame received from the device — both matched command-ACK
   * responses (BLINK/MEASURE_ABORT/MEASURE_RESULT/getState) and unsolicited/watchdog/progress frames
   * (harness communication logging, Story 1.3). This is a superset of
   * `onUnsolicitedMessage`, which only covers the watchdog path; the harness needs every
   * inbound frame so its communication log is symmetric with the sent side.
   * Returns an unsubscribe function.
   */
  public onReceivedMessage(
    handler: (raw: Uint8Array, msg: DevMessage, crcValid: boolean) => void
  ): () => void {
    this._receivedHandlers.push(handler);
    return () => {
      this._receivedHandlers = this._receivedHandlers.filter((h) => h !== handler);
    };
  }

  /**
   * Notify onReceivedMessage subscribers of a frame that was just received and decoded,
   * regardless of how it is subsequently routed (command resolver vs watchdog handlers).
   * `crcValid` is false for a frame whose CRC32 failed but was still decoded — subscribers
   * (the harness Communication Log) persist it either way so the incident is traceable.
   */
  private notifyReceived(raw: Uint8Array, msg: DevMessage, crcValid = true): void {
    for (const handler of this._receivedHandlers) {
      try {
        handler(raw, msg, crcValid);
      } catch (e) {
        this.logger.warn('USB RX', 'Received-message handler threw', e);
      }
    }
  }

  /**
   * Subscribe to transferIn failure episodes on the background receive loop.
   * Returns an unsubscribe function.
   */
  public onTransferIncident(handler: (incident: TransferIncident) => void): () => void {
    this._transferIncidentHandlers.push(handler);
    return () => {
      this._transferIncidentHandlers = this._transferIncidentHandlers.filter((h) => h !== handler);
    };
  }

  /**
   * Notify onTransferIncident subscribers of a started/recovered/failed transfer episode.
   */
  private notifyTransferIncident(incident: TransferIncident): void {
    for (const handler of this._transferIncidentHandlers) {
      try {
        handler(incident);
      } catch (e) {
        this.logger.warn('USB RX', 'Transfer-incident handler threw', e);
      }
    }
  }

  /** Count of inbound frames whose CRC32 failed but were decoded and used anyway (diagnostics). */
  public get crcFallbackUsedCount(): number {
    return this._crcFallbackUsedCount;
  }

  /**
   * Resend the last response from the device.
   * The device replays the last outbound frame it transmitted — this may be
   * a measurement completion frame with impedanceResult, a progress frame,
   * or the ACK of the last command.
   */
  public async resend(): Promise<DevMessage | null> {
    return this.sendCommand(Triggers.MEASURE_RESULT);
  }

  /**
   * Attempt to reconnect to a previously-paired Ediphor device.
   *
   * Uses navigator.usb.getDevices() which returns devices the user has
   * previously granted permission to — no new permission prompt required.
   *
   * @returns true if reconnection and initial state query succeeded
   */
  public async tryAutoReconnect(): Promise<boolean> {
    logger.debug('[RECOVERY:Device] tryAutoReconnect() called');
    if (this._autoReconnectInProgress) {
      logger.debug('[RECOVERY:Device] tryAutoReconnect: already in progress — skipping concurrent call');
      return false;
    }
    if (!this.isWebUSBSupported()) {
      logger.debug('[RECOVERY:Device] WebUSB not supported — cannot auto-reconnect');
      return false;
    }
    this._autoReconnectInProgress = true;

    try {
      const devices = await navigator.usb.getDevices();
      logger.debug(`[RECOVERY:Device] getDevices() → ${devices.length} paired device(s):`,
        devices.map(d => `${d.vendorId.toString(16).padStart(4,'0')}:${d.productId.toString(16).padStart(4,'0')} (${d.productName ?? 'unnamed'})`));
      const ediphorDevice = devices.find(
        (d) =>
          d.vendorId === this.options.vendorId &&
          this.options.productIds.includes(d.productId)
      );

      if (!ediphorDevice) {
        logger.debug(`[RECOVERY:Device] No Ediphor device found — looking for vendorId=0x${this.options.vendorId.toString(16)} productIds=[${this.options.productIds.map(id => '0x' + id.toString(16)).join(', ')}]`);
        return false;
      }

      logger.debug(`[RECOVERY:Device] Found device: ${ediphorDevice.productName ?? 'unnamed'} (${ediphorDevice.vendorId.toString(16)}:${ediphorDevice.productId.toString(16)}) — reconnecting...`);
      this.device = ediphorDevice;

      await this._initialiseOpenDevice();

      logger.debug('[RECOVERY:Device] tryAutoReconnect: SUCCESS ✓');
      return true;
    } catch (error) {
      logger.warn('[RECOVERY:Device] tryAutoReconnect: FAILED ✗', error);
      this.device = null;
      this._transportOverride = null;
      return false;
    } finally {
      this._autoReconnectInProgress = false;
    }
  }

  /**
   * Subscribe to push frames from an already-running measurement.
   * Used for recovery when MEASURE_RESULT reports processingId > 0 (device still measuring).
   *
   * Unlike runMeasurement(), this does NOT send a MEASURE_START trigger —
   * it only listens to the unsolicited message stream.
   */
  public async subscribeMeasurement(options: {
    onProgress?: (progress: number) => void;
    onStateUpdate?: (state: import('@/types/device').DeviceState) => void;
    abortSignal?: AbortSignal;
  }): Promise<DevMessage> {
    logger.debug('[RECOVERY:Device] subscribeMeasurement() called — device connected:', !!this.device, '| callbacks:', Object.keys(options).filter(k => !!(options as any)[k]).join(', ') || 'none');
    if (!this.device) {
      logger.warn('[RECOVERY:Device] subscribeMeasurement: no device instance — throwing CONNECTION_LOST');
      throw createDeviceError(DeviceErrorCode.CONNECTION_LOST);
    }

    return this.awaitMeasurementCompletion({
      onProgress: options.onProgress,
      abortSignal: options.abortSignal,
      onStateUpdate: options.onStateUpdate
        ? (_busy: boolean, progress: number) => {
            // Build a minimal DeviceState for the callback
            const store = useDeviceStore.getState();
            options.onStateUpdate!(store.deviceState);
            return progress;
          }
        : undefined,
    });
  }

  /**
   * Return the raw wire bytes of the most recent command response.
   *
   * Call this immediately after any `blink()` / `getState()` /
   * `sendCustomCommand()` to get the full frame that was received from the
   * device including framing bytes and CRC.  Returns `null` if no command has
   * completed yet, or if the last command ended in an error.
   */
  public getLastRawResponse(): Uint8Array | null {
    return this._lastCmdRaw;
  }

  /**
   * Internal helper that handles an incoming unsolicited / watchdog frame.
   *
   * Exposed publicly so that unit tests can drive it without needing a full
   * USB connection loop.
   */
  public handleWatchdogMessage(
    devMessage: DevMessage,
    raw: Uint8Array,
    hexDump?: string
  ): void {
    const ts = new Date().toISOString().replace('T', ' ').split('.')[0];
    const dump =
      hexDump ??
      Array.from(raw)
        .map((b) => b.toString(16).padStart(2, '0').toUpperCase())
        .join(' ');
    this.logger.trace('USB RX (WD)', `← WATCHDOG ${raw.length} bytes @ ${ts}`, {
      byteLength: raw.length,
      hexDump: dump,
      message: devMessage,
    });
    logger.debug(
      '%c[USB RX (WD)] ← WATCHDOG received',
      'color: #ff6600; font-weight: bold;',
      devMessage
    );

    // Keep device state in sync with watchdog data regardless of logging
    this.updateDeviceState(devMessage);

    // Dispatch to subscribers (e.g. DeviceTestPage)
    for (const handler of this._unsolicitedHandlers) {
      try {
        handler(devMessage, raw);
      } catch (e) {
        this.logger.warn('USB RX (WD)', 'Unsolicited handler threw', e);
      }
    }
  }

  /**
   * Run a measurement with the given profile
   */
  public async runMeasurement(
    options: MeasurementOptions
  ): Promise<MeasurementResult> {
    const store = useDeviceStore.getState();

    if (!this.device) {
      throw createDeviceError(DeviceErrorCode.CONNECTION_LOST);
    }

    // Check device is ready
    if (store.deviceState.busy) {
      throw createDeviceError(DeviceErrorCode.DEVICE_BUSY);
    }

    if (!store.deviceState.electrodeDetected) {
      throw createDeviceError(DeviceErrorCode.ELECTRODE_NOT_DETECTED);
    }

    const startTime = Date.now();
    store.setConnectionState('busy');
    store.updateMeasurementProgress({
      isRunning: true,
      progress: 0,
      startedAt: new Date(),
    });

    try {
      if (!options.impedanceExec) {
        throw createDeviceError(
          DeviceErrorCode.INVALID_PROFILE,
          'Measurement profile is required to start a measurement'
        );
      }

      // Points sent in one MEASURE_START reply are shared by every requested
      // channel, bias, amplitude and frequency, times the interval repetitions
      // (an empty intervalMs — today's normal placeholder — still counts as one
      // pass, hence max(1, ...)). Firmware rejects MEASURE_START outright if this
      // exceeds its reported Limits.points_max, so fail fast here instead of
      // burning a USB round-trip on a request the device will refuse.
      const { channels, biasMV, amplitudeMV, frequencyHz, intervalMs } = options.impedanceExec;
      const computedPointCount =
        channels.length *
        biasMV.length *
        amplitudeMV.length *
        frequencyHz.length *
        Math.max(1, intervalMs.length);

      // `pointsMax` is a bare proto3 uint32: a decoded `0` is indistinguishable on the
      // wire from "field omitted." A real device can never sensibly report a 0-point
      // capacity, so treat a cached 0 the same as "not yet known" (fail open) — only a
      // genuinely positive, reported limit should block submission.
      if (
        store.deviceState.pointsMax !== null &&
        store.deviceState.pointsMax > 0 &&
        computedPointCount > store.deviceState.pointsMax
      ) {
        throw createDeviceError(
          DeviceErrorCode.POINTS_LIMIT_EXCEEDED,
          `Measurement requires ${computedPointCount} points, exceeding the device's limit of ${store.deviceState.pointsMax}`
        );
      }

      // MEASURE_START is correlated through the same resolver queue as every other
      // trigger (AD-3): it settles on either an ACK echo or the first frame whose
      // state.processingId equals the minted id. Settling is additive — that frame is
      // still forwarded to the watchdog handlers. Subscribe to completion FIRST so the
      // settling frame is never missed.
      const completion = this.beginMeasurementCompletion(options);
      let result: DevMessage;
      try {
        const start = this.sendCommand(
          Triggers.MEASURE_START,
          options.impedanceExec,
          TIMING.MEASUREMENT_STALE_TIMEOUT
        ).then(() => undefined);
        // If completion wins the race (abort, or it finished off the settling frame),
        // a later start failure has no one left to report to.
        start.catch(() => {});
        await Promise.race([start, completion.promise]);
        result = await completion.promise;
      } catch (error) {
        completion.cancel();
        completion.promise.catch(() => {});
        if (
          typeof error === 'object' &&
          error !== null &&
          'code' in error &&
          (error as { code: unknown }).code === DeviceErrorCode.RECEIVE_TIMEOUT
        ) {
          throw createDeviceError(
            DeviceErrorCode.MEASUREMENT_TIMEOUT,
            'No response from device for ' + TIMING.MEASUREMENT_STALE_TIMEOUT + ' ms — device may have stalled'
          );
        }
        throw error;
      } finally {
        // Whatever won the race, no MEASURE_START resolver may outlive this call.
        for (const [id, trig] of [...this._pendingTriggerByCorrelationId]) {
          if (trig === Triggers.MEASURE_START) this._cancelResolver(id);
        }
      }

      // Create result
      const measurementResult: MeasurementResult = {
        deviceUid: store.deviceInfo!.uid,
        deviceSerialNumber: store.deviceInfo!.serialNumber,
        firmwareVersion: store.deviceInfo!.firmwareVersion,
        hardwareVersion: store.deviceInfo!.hardwareVersion,
        durationMs: Date.now() - startTime,
        impedanceResult: result.impedanceResult!,
        errorCode: result.errorCode,
        completedAt: new Date(),
      };

      store.setMeasurementResult(measurementResult);
      store.setConnectionState('connected');

      return measurementResult;
    } catch (error) {
      // Only reset to 'connected' if the device is actually still connected.
      // handleDisconnect may have already set connectionState to 'disconnected' or
      // 'error' (setError overwrites to 'error' immediately after setConnectionState).
      // Overwriting either of those would hide the disconnect from callers.
      const liveState = useDeviceStore.getState().connectionState;
      if (liveState !== 'disconnected' && liveState !== 'error') {
        store.setConnectionState('connected');
      }
      store.updateMeasurementProgress({ isRunning: false, progress: 0 });

      // DeviceErrors are plain objects (not class instances), so `instanceof Error` always
      // returns false for them. Check the object shape instead.
      const isPlainDeviceError =
        typeof error === 'object' &&
        error !== null &&
        'code' in error &&
        'message' in error &&
        'recoverable' in error;
      if (isPlainDeviceError) {
        throw error; // Already a DeviceError — preserve original code and message
      }
      throw classifyError(error);
    }
  }

  /**
   * Cancel the current measurement
   */
  public async cancelMeasurement(): Promise<void> {
    // MEASURE_ABORT gets an ACK reply — use the normal sendCommand path
    try {
      await this.sendCommand(Triggers.MEASURE_ABORT);
    } catch {
      // Ignore cancel errors
    }

    const store = useDeviceStore.getState();
    store.updateMeasurementProgress({ isRunning: false, progress: 0 });
    store.setConnectionState('connected');
  }

  // ========== Private Methods ==========

  /**
   * Send a command to the device and wait for response
   */
  private async sendCommand(
    trigger: Triggers,
    impedanceExec?: ImpedanceExec,
    responseTimeoutMs?: number
  ): Promise<DevMessage | null> {
    if (!this.device) {
      throw createDeviceError(DeviceErrorCode.CONNECTION_LOST);
    }

    const message = createAppMessage(trigger, impedanceExec);
    // message.id is a client-issued bigint (wire requirement) but always well within
    // Number.MAX_SAFE_INTEGER — narrow it once here so correlation bookkeeping stays
    // in plain-number land, matching the ids the device echoes back.
    const correlationId = Number(message.id);
    // Record which trigger this correlation id belongs to before anything can
    // resolve/reject it, so _receiveLoop can gate the recoverable-partial-result
    // path to only MEASURE_ABORT/MEASURE_RESULT.
    this._pendingTriggerByCorrelationId.set(correlationId, trigger);
    const encoded = encodeAppMessage(message);

    const triggerName = Triggers[trigger] ?? `UNKNOWN(${trigger})`;
    this._lastCmdRaw = null; // clear before every new command
    this.logger.trace('USB TX', `→ [${triggerName}] ${encoded.length} bytes`, {
      triggerName,
      byteLength: encoded.length,
      message,
      encodedHex: Array.from(encoded)
        .map((b) => b.toString(16).padStart(2, '0').toUpperCase())
        .join(' '),
    });

    // Register the resolver BEFORE transferOut so a reply that lands while the write
    // is still settling is never dropped as "no resolver waiting".
    const responsePromise = this.receiveResponse(correlationId, responseTimeoutMs);
    // If the send fails we abandon this promise; if the resolver rejects first (e.g.
    // disconnect) before we await it, don't surface that as an unhandled rejection.
    responsePromise.catch(() => {});

    try {
      // Send command - create a new ArrayBuffer to ensure proper type
      const buffer = new ArrayBuffer(encoded.byteLength);
      new Uint8Array(buffer).set(encoded);
      const sendResult = await this.transport!.transferOut(
        this.outEndpoint,
        buffer
      );

      if (sendResult.status !== 'ok') {
        this.logger.warn('USB TX', `transferOut status: ${sendResult.status}`);
        throw createDeviceError(DeviceErrorCode.SEND_FAILED);
      }

      this.notifySent(encoded, message);

      // Wait for response — filter by message ID to skip status broadcasts
      const response = await responsePromise;

      // ⚠️ CRITICAL: Validate acknowledgement (trigger/field-level sanity check)
      if (response) {
        const isValid = validateAcknowledgement(message, response);
        if (!isValid) {
          this.logger.error('USB RX', 'Acknowledgement mismatch', { sent: trigger, received: response });
          throw createDeviceError(
            DeviceErrorCode.ACKNOWLEDGEMENT_MISMATCH,
            'Device did not correctly acknowledge command - possible communication corruption'
          );
        }
        this.logger.trace('USB TX', `ACK OK for ${triggerName}`);
      }

      return response;
    } catch (error) {
      this._cancelResolver(correlationId); // no-op if already settled/timed out
      this.logger.error('USB TX', `Command failed: ${triggerName}`, error);
      if (error && typeof error === 'object' && 'code' in error) {
        throw error; // Already a DeviceError
      }
      throw classifyError(error);
    }
  }

  /**
   * Wait for a command response from the device that matches the given message ID.
   * The background receive loop delivers only the matching response; unsolicited
   * status broadcasts and responses with wrong IDs are dispatched to
   * updateDeviceState() and discarded as command responses.
   *
   * @param expectedId - The AppMessage.id that was sent; the response must echo this value.
   * @param timeoutMs - Optional override for the resolver timeout (defaults to the command timeout).
   */
  private receiveResponse(expectedId: number, timeoutMs?: number): Promise<DevMessage | null> {
    if (!this.device) {
      return Promise.resolve(null);
    }

    return new Promise<DevMessage | null>((resolve, reject) => {
      const entry: {
        resolve: (msg: DevMessage | null) => void;
        reject: (err: unknown) => void;
        timer: ReturnType<typeof setTimeout>;
        expectedId: number;
      } = {
        resolve,
        reject,
        expectedId,
        timer: setTimeout(() => {
          const idx = this._rxResolvers.indexOf(entry);
          if (idx >= 0) this._rxResolvers.splice(idx, 1);
          this._pendingTriggerByCorrelationId.delete(expectedId);
          this.logger.warn('USB RX', `receiveResponse: timeout waiting for id=${expectedId} — no matching response from device`);
          reject(createDeviceError(DeviceErrorCode.RECEIVE_TIMEOUT));
        }, timeoutMs ?? this.options.timeouts.command),
      };

      this._rxResolvers.push(entry);
    });
  }

  /** Drop a pending resolver without settling it (send failed, so no reply can come). */
  private _cancelResolver(expectedId: number): void {
    const idx = this._rxResolvers.findIndex((e) => e.expectedId === expectedId);
    if (idx >= 0) {
      clearTimeout(this._rxResolvers[idx].timer);
      this._rxResolvers.splice(idx, 1);
    }
    this._pendingTriggerByCorrelationId.delete(expectedId);
  }

  // ========== Background Receive Loop ==========

  /**
   * Start the single background USB receive pump.
   * Must be called after the interface is claimed.
   */
  private _startReceiveLoop(): void {
    if (this._bgLoopAbort) {
      this._bgLoopAbort.abort();
    }
    this._frameAssembler.reset(); // fresh connection: no stale partial frame
    this._bgLoopAbort = new AbortController();
    void this._receiveLoop(this._bgLoopAbort.signal);
  }

  /**
   * Stop the background receive loop and reject any pending command resolvers.
   */
  private _stopReceiveLoop(): void {
    this._bgLoopAbort?.abort();
    this._bgLoopAbort = null;
    this._frameAssembler.reset(); // disconnect / connection loss: drop any partial frame

    for (const entry of this._rxResolvers) {
      clearTimeout(entry.timer);
      this._pendingTriggerByCorrelationId.delete(entry.expectedId);
      entry.reject(createDeviceError(DeviceErrorCode.CONNECTION_LOST));
    }
    this._rxResolvers = [];
  }

  /**
   * The single receive pump — reads from the IN endpoint continuously.
   * Delivers messages to waiting command resolvers, or logs them as
   * unsolicited / watchdog messages when no command is in flight.
   */
  private async _receiveLoop(signal: AbortSignal): Promise<void> {
    this.logger.info('USB RX', 'Background receive loop started');

    while (!signal.aborted && this.device) {
      try {
        const result = await this.transport!.transferIn(
          this.inEndpoint,
          EDIPHOR_USB.MAX_MESSAGE_SIZE
        );

        // transferIn succeeded — if we were mid-episode, report recovery before continuing.
        if (this._consecutiveTransferErrors > 0) {
          const durationMs = Date.now() - (this._transferIncidentStartedAt ?? Date.now());
          this.logger.info(
            'USB RX',
            `transferIn recovered after ${this._consecutiveTransferErrors} failed attempt(s) (${durationMs}ms)`
          );
          this.notifyTransferIncident({
            phase: 'recovered',
            consecutiveErrors: this._consecutiveTransferErrors,
            durationMs,
            errorMessage: '',
          });
          this._consecutiveTransferErrors = 0;
          this._transferIncidentStartedAt = null;
        }

        if (signal.aborted || !this.device) break;

        if (result.status !== 'ok' || !result.data) {
          this.logger.warn('USB RX', `transferIn non-ok: ${result.status}`);
          continue;
        }

        // Reassemble: a DevMessage may span several transferIn results, and one result may
        // carry several frames. Every complete payload is re-framed so downstream code keeps
        // seeing the same full-frame `raw` bytes it always did.
        const chunk = new Uint8Array(result.data.buffer, result.data.byteOffset, result.data.byteLength);
        for (const payload of this._frameAssembler.feed(chunk)) {
          if (signal.aborted || !this.device) break;
          this._processFrame(frameBytes(payload), signal);
        }
      } catch (error) {
        if (signal.aborted) break;

        this._consecutiveTransferErrors++;
        const errorMessage = error instanceof Error ? error.message : String(error);

        if (this._consecutiveTransferErrors === 1) {
          this._transferIncidentStartedAt = Date.now();
          this.notifyTransferIncident({
            phase: 'started',
            consecutiveErrors: 1,
            durationMs: 0,
            errorMessage,
          });
        }

        this.logger.warn('USB RX', 'Receive loop transfer error (will retry in 100 ms)', error);

        // Best-effort stall recovery — a halted bulk-IN endpoint is the most common reason a
        // still-present device stops answering transferIn. Swallow failures: clearHalt itself
        // throws if the device is genuinely gone, and the loop's own retry/escalation logic
        // already handles that case.
        // A failed transfer may have truncated a frame mid-stream; drop the partial bytes so
        // the next frame doesn't get glued onto them.
        this._frameAssembler.reset();
        try {
          await this.transport?.clearHalt('in', this.inEndpoint);
        } catch {
          // ignored — see comment above
        }

        if (this._consecutiveTransferErrors >= TIMING.MAX_CONSECUTIVE_TRANSFER_ERRORS) {
          const durationMs = Date.now() - (this._transferIncidentStartedAt ?? Date.now());
          this.notifyTransferIncident({
            phase: 'failed',
            consecutiveErrors: this._consecutiveTransferErrors,
            durationMs,
            errorMessage,
          });
          this._handleUnrecoverableTransferFailure();
          break;
        }

        await new Promise((r) => setTimeout(r, 100));
      }
    }

    this.logger.info('USB RX', 'Background receive loop stopped');
  }

  /**
   * Handle one complete, framed inbound frame (`raw` = header + protobuf + CRC32).
   * Decodes it and routes it to a command resolver or the watchdog path.
   */
  private _processFrame(raw: Uint8Array, signal: AbortSignal): void {
    // Full hex dump of every received byte — no truncation
    const hexDump = Array.from(raw)
      .map((b) => b.toString(16).padStart(2, '0').toUpperCase())
      .join(' ');

    const decodeResult = tryDecodeDevMessageWithCrcStatus(raw);
    if (!decodeResult) {
      this.logger.warn('USB RX', `← Failed to decode ${raw.length} bytes`, { hexDump });
      return;
    }

    if (!decodeResult.crcValid) {
      this._crcFallbackUsedCount++;
      this.logger.warn(
        'USB RX',
        `← CRC32 mismatch on ${raw.length}-byte frame (received 0x${decodeResult.receivedCrc
          .toString(16)
          .padStart(8, '0')}, calculated 0x${decodeResult.calculatedCrc
          .toString(16)
          .padStart(8, '0')}) — ` +
          `${CRC_FALLBACK.ALLOW_DECODE_ON_MISMATCH ? `decoding anyway (fallback use #${this._crcFallbackUsedCount})` : 'discarding'}`,
        { hexDump, message: decodeResult.message }
      );

      // Always notify (and therefore persist to the harness Communication Log) so the
      // incident is traceable and countable, independent of whether the fallback content
      // is actually used below.
      this.notifyReceived(raw, decodeResult.message, false);

      if (!CRC_FALLBACK.ALLOW_DECODE_ON_MISMATCH) {
        return;
      }
    } else {
      // Notify received-frame subscribers for EVERY decoded inbound frame, exactly once,
      // before routing to a command resolver or the watchdog path — so the harness
      // communication log captures command ACK responses too, not just watchdog frames.
      this.notifyReceived(raw, decodeResult.message, true);
    }

    const devMessage = decodeResult.message;

    // The single errorCode check for frames that do NOT settle a resolver: a resolver-settling
    // frame is handled (resolve/reject) where it matches; anything else — e.g. a refusal or
    // error arriving after MEASURE_START already settled — is logged only, never re-settled.
    const settledResolver = this._routeDecodedFrame(raw, devMessage, hexDump, signal);
    if (!settledResolver && hasError(devMessage)) {
      this.logger.warn(
        'USB RX',
        `Device error code: ${devMessage.errorCode} on a frame that settles no pending command (${getErrorMessage(devMessage.errorCode)})`,
        devMessage
      );
    }
  }

  /**
   * Route a decoded frame to a command resolver or the watchdog path.
   * @returns true when the frame settled (resolved or rejected) a pending resolver.
   */
  private _routeDecodedFrame(
    raw: Uint8Array,
    devMessage: DevMessage,
    hexDump: string,
    signal: AbortSignal
  ): boolean {
    if (this._rxResolvers.length > 0) {
      // Always update device state — every received message is useful.
      this.updateDeviceState(devMessage);

      // Determine the correlation ID for this message.
      // Primary:  appMessage.id — present only on ACK replies (BLINK/SLEEP/REBOOT/
      //            MEASURE_RESULT/MEASURE_ABORT).  Must take unconditional priority because the device
      //            may still report a non-zero processingId on those replies when a
      //            concurrent MEASURE_START is in flight — using processingId first would
      //            mis-route the ACK to the wrong resolver and cause a timeout.
      // Fallback: state.processingId — non-zero value identifies which MEASURE_START
      //            request the device is executing.  Used for progress frames that
      //            carry no appMessage at all.
      // Both are wire bigints but always well within Number.MAX_SAFE_INTEGER — narrow
      // to number here so matching against `expectedId` uses plain numeric equality.
      const processingId = Number(devMessage.state?.processingId ?? 0n);
      const correlationId =
        devMessage.appMessage?.id !== undefined
          ? Number(devMessage.appMessage.id)
          : processingId !== 0
            ? processingId
            : undefined;

      if (!correlationId) {
        // No correlation ID — treat as status broadcast; keep waiting.
        const oldestId = this._rxResolvers[0].expectedId;
        this.logger.trace(
          'USB RX',
          `← Status broadcast (no correlation id), waiting for id=${oldestId}`,
          { byteLength: raw.length, hexDump, message: devMessage }
        );
        // Treat as watchdog so listeners get the update.
        this.handleWatchdogMessage(devMessage, raw, hexDump);
        return false;
      }

      // Search ALL pending resolvers for the one matching this correlation ID.
      // Only peeking at [0] caused valid responses to be discarded when an
      // orphaned/stale resolver was stuck ahead in the queue (bug: device
      // replied with the correct ID but it was dropped as "mismatch").
      const matchIdx = this._rxResolvers.findIndex(
        (e) => e.expectedId === correlationId
      );

      if (matchIdx === -1) {
        // If this is a pure measurement progress frame (processingId-only,
        // no appMessage.id) it is a watchdog push — not an ACK — and must
        // be forwarded to unsolicited handlers even when a command resolver
        // is pending. Discarding it would starve awaitMeasurementCompletion
        // of the frames it needs to detect completion and make the
        // measurement hang until MEASUREMENT_STALE_TIMEOUT fires.
        if (!devMessage.appMessage?.id && processingId !== 0) {
          this.logger.trace(
            'USB RX',
            `← Progress frame (processingId=${devMessage.state?.processingId}) while command pending — routing to watchdog handlers`,
            { byteLength: raw.length, hexDump, message: devMessage }
          );
          this.handleWatchdogMessage(devMessage, raw, hexDump);
          return false;
        }
        // Truly unexpected — no resolver waiting for this correlation ID at all.
        this.logger.warn(
          'USB RX',
          `← Unexpected response correlationId=${correlationId} — no resolver waiting, discarding`,
          { byteLength: raw.length, hexDump, message: devMessage }
        );
        return false;
      }

      if (matchIdx > 0) {
        // The matching resolver was not at the front — there are stale
        // orphaned resolvers ahead of it.  Log a warning so the issue is
        // visible; those orphans will time out on their own.
        const staleIds = this._rxResolvers.slice(0, matchIdx).map((e) => e.expectedId);
        this.logger.warn(
          'USB RX',
          `← ID matched at queue position ${matchIdx} (stale orphan ids ahead: [${staleIds.join(', ')}]) — delivering out-of-order`,
          { byteLength: raw.length, hexDump, message: devMessage }
        );
      }

      // Matched — consume the resolver and deliver.
      const [entry] = this._rxResolvers.splice(matchIdx, 1);
      clearTimeout(entry.timer);

      // Look up (and clear) which trigger this correlation id was sent for.
      // Only MEASURE_ABORT/MEASURE_RESULT can legitimately carry a partial
      // impedanceResult; BLINK/SLEEP/REBOOT never do, so they must never be
      // treated as recoverable even if their error_code happens to unpack to
      // module=3, code=0x02.
      const sentTrigger = this._pendingTriggerByCorrelationId.get(entry.expectedId);
      this._pendingTriggerByCorrelationId.delete(entry.expectedId);

      this.logger.trace('USB RX', `← CMD response id=${entry.expectedId} ${raw.length} bytes`, {
        byteLength: raw.length,
        hexDump,
        message: devMessage,
      });

      if (hasError(devMessage)) {
        // MEASURE_START joins the set: a recoverable code on it resolves (not rejects),
        // per AD-3, so the sweep is not failed on a code that still carries valid data.
        const canCarryPartialResult =
          sentTrigger === Triggers.MEASURE_ABORT ||
          sentTrigger === Triggers.MEASURE_RESULT ||
          sentTrigger === Triggers.MEASURE_START;
        if (isRecoverableError(devMessage.errorCode) && canCarryPartialResult) {
          // e.g. a MEASURE_ABORT-interrupted sweep (module 3, code 0x02): the
          // response still carries a valid partial impedanceResult worth
          // delivering to the caller rather than discarding as a hard failure.
          this.logger.warn(
            'USB RX',
            `Device error code: ${devMessage.errorCode} — recoverable (partial result), resolving with partial impedanceResult`,
            devMessage
          );
          this._lastCmdRaw = raw; // keep raw bytes accessible for diagnostics
          entry.resolve(devMessage);
        } else {
          this.logger.warn('USB RX', `Device error code: ${devMessage.errorCode}`, devMessage);
          entry.reject(
            createDeviceError(
              DeviceErrorCode.DEVICE_ERROR,
              getErrorMessage(devMessage.errorCode),
              true,
              undefined,
              devMessage.errorCode
            )
          );
        }
      } else {
        this._lastCmdRaw = raw; // keep raw bytes accessible for diagnostics
        entry.resolve(devMessage);
      }

      // AD-3: settling MEASURE_START's resolver is additive bookkeeping, never exclusive
      // routing — the settling frame (ACK echo or first processingId frame) still goes to
      // the watchdog handlers so awaitMeasurementCompletion never misses it.
      if (sentTrigger === Triggers.MEASURE_START) {
        this.handleWatchdogMessage(devMessage, raw, hexDump);
      }
      return true;
    } else {
      // Unsolicited / watchdog — no command is waiting for a response
      // Guard before any logging or state mutation
      if (signal.aborted || !this.device) return false;

      // delegate to helper so we can easily test this logic
      this.handleWatchdogMessage(devMessage, raw, hexDump);
      return false;
    }
  }

  /**
   * Called when the receive loop's transferIn has failed
   * TIMING.MAX_CONSECUTIVE_TRANSFER_ERRORS times in a row with no successful recovery.
   * Treats the connection as lost — same cleanup as handleDisconnect — so the existing
   * reconnect UI (DeviceReconnectDialog / MeasurementInterruptedModal) takes over instead
   * of the loop spinning silently forever.
   */
  private _handleUnrecoverableTransferFailure(): void {
    this.logger.warn(
      'USB RX',
      `Giving up after ${this._consecutiveTransferErrors} consecutive transferIn failures — treating connection as lost`
    );

    this._consecutiveTransferErrors = 0;
    this._transferIncidentStartedAt = null;
    this.device = null;
    this._transportOverride = null;

    // Reuses _stopReceiveLoop's resolver-rejection + abort-controller cleanup. Aborting our
    // own already-exiting loop's signal here is harmless — we break out of _receiveLoop
    // immediately after this call returns.
    this._stopReceiveLoop();

    const store = useDeviceStore.getState();
    store.setConnectionState('disconnected');
    store.setError(createDeviceError(DeviceErrorCode.CONNECTION_LOST, 'Device stopped responding'));
  }

  /**
   * Wait for measurement completion by listening to unsolicited push messages from the device.
   *
   * The device sends watchdog frames at its own cadence during measurement (no polling needed).
   * This method:
   *   - Subscribes to `onUnsolicitedMessage` to receive every push update
   *   - Resolves when the device reports `busy=false && progress===100` with an impedance result
   *   - Rejects with MEASUREMENT_TIMEOUT if the device is silent for MEASUREMENT_STALE_TIMEOUT ms
   *   - Rejects with MEASUREMENT_TIMEOUT if the total duration exceeds `options.timeouts.measurement`
   *   - Rejects with MEASUREMENT_CANCELLED if `options.abortSignal` fires
   */
  private awaitMeasurementCompletion(
    options: MeasurementOptions
  ): Promise<DevMessage> {
    return this.beginMeasurementCompletion(options).promise;
  }

  /**
   * Same as {@link awaitMeasurementCompletion}, but also returns a `cancel()` handle that
   * tears down the listener, timers and abort hook without settling the promise. The
   * subscription is live as soon as this returns, so callers can subscribe *before*
   * sending MEASURE_START and never miss the frame that settles its correlation.
   */
  private beginMeasurementCompletion(
    options: MeasurementOptions
  ): { promise: Promise<DevMessage>; cancel: () => void } {
    const store = useDeviceStore.getState();
    const startTime = Date.now();
    let cancel: () => void = () => {};

    const promise = new Promise<DevMessage>((resolve, reject) => {
      let staleTimer: ReturnType<typeof setTimeout> | null = null;
      let maxedProgress = false;  // Track whether we've seen progress >= 100
      let lastProcessingId = 0;   // Track processingId to detect transition to idle
      let lastImpedanceResult: ImpedanceResult | undefined = undefined;

      // Abort signal listener
      const onAbort = () => {
        cleanup();
        reject(createDeviceError(DeviceErrorCode.MEASUREMENT_CANCELLED));
      };
      options.abortSignal?.addEventListener('abort', onAbort);

      // Max-duration guard — rejects if measurement takes too long overall
      const maxTimer = setTimeout(() => {
        cleanup();
        reject(createDeviceError(
          DeviceErrorCode.MEASUREMENT_TIMEOUT,
          'Measurement exceeded maximum allowed duration'
        ));
      }, this.options.timeouts.measurement);

      const resetStaleTimer = () => {
        if (staleTimer) clearTimeout(staleTimer);
        staleTimer = setTimeout(() => {
          cleanup();
          reject(createDeviceError(
            DeviceErrorCode.MEASUREMENT_TIMEOUT,
            'No response from device for ' + TIMING.MEASUREMENT_STALE_TIMEOUT + ' ms — device may have stalled'
          ));
        }, TIMING.MEASUREMENT_STALE_TIMEOUT);
      };

      const cleanup = () => {
        if (staleTimer) clearTimeout(staleTimer);
        clearTimeout(maxTimer);
        options.abortSignal?.removeEventListener('abort', onAbort);
        unsubscribe();
      };

      cancel = cleanup;

      // Subscribe to unsolicited push messages from the device
      const unsubscribe = this.onUnsolicitedMessage((msg) => {
        // Device is alive — reset the stale-detection timer
        resetStaleTimer();

        const progress = msg.state?.progressPct ?? 0;
        // processingId is a client-issued correlation id (see sendCommand), always well
        // within Number.MAX_SAFE_INTEGER — safe to narrow from the wire's bigint here.
        const currentProcessingId = Number(msg.state?.processingId ?? 0n);

        store.updateMeasurementProgress({
          progress,
          estimatedTimeRemaining: this.estimateTimeRemaining(progress, startTime),
        });
        options.onProgress?.(progress);
        options.onStateUpdate?.(currentProcessingId !== 0, progress);

        // Track progress milestone
        if (progress >= 100) {
          maxedProgress = true;
        }

        // Accumulate impedanceResult — device may send it in a different message than the
        // processingId-transition message, so we need to track the last seen value.
        if (msg.impedanceResult) {
          lastImpedanceResult = msg.impedanceResult;
        }

        // Detect completion: processingId transition from non-zero to zero AND progress maxed
        const wasProcessing = lastProcessingId !== 0;
        const isNowIdle = currentProcessingId === 0;
        const transitioned = wasProcessing && isNowIdle;

        // Resolve when device signals completion:
        //   1. processingId transitions from non-zero to zero (device idle), AND progress reached 100
        //   2. OR impedanceResult is present (measurement result available)
        if ((transitioned && maxedProgress) || msg.impedanceResult) {
          cleanup();
          // Merge accumulated impedanceResult in case it arrived in an earlier message
          resolve({ ...msg, impedanceResult: msg.impedanceResult ?? lastImpedanceResult });
        }

        lastProcessingId = currentProcessingId;
      });

      // Start stale timer — first watchdog expected within MEASUREMENT_STALE_TIMEOUT ms
      resetStaleTimer();

      this.logger.info('DeviceService', 'awaitMeasurementCompletion: listening for push updates from device');
    });

    return { promise, cancel: () => cancel() };
  }

  /**
   * Update device state in store from DevMessage
   */
  private updateDeviceState(devMessage: DevMessage): void {
    // .limits is a sibling field of .state, present on every DevMessage per its
    // doc comment — cache it independent of whether .state is present, so the
    // pointsMax check in runMeasurement stays current even from state-less frames.
    if (devMessage.limits) {
      useDeviceStore.getState().updateDeviceState({
        pointsMax: devMessage.limits.pointsMax,
      });
    }

    if (!devMessage.state) return;

    const store = useDeviceStore.getState();
    const state = devMessage.state;

    const electrodeType = (): DeviceState['electrodeType'] => {
      // v1.4.4's ElectrodeDetect.CODE_1–CODE_7 have no known physical electrode
      // mapping yet (blocked on hardware/EE). Reporting a guessed channel-count
      // label here would be actively wrong, so surface an explicit unknown
      // state instead — including for NC, which is already reflected by
      // `electrodeDetected` below.
      return 'unknown';
    };

    const powerState = (): DeviceState['powerState'] => {
      switch (state.powerState) {
        case PowerState.BATTERY:
          return 'battery';
        case PowerState.CHARGING:
          return 'charging';
        case PowerState.STANDBY:
          return 'standby';
        case PowerState.FAULT:
          return 'fault';
        case PowerState.NO_BATTERY:
          return 'no_battery';
        default:
          return 'unknown';
      }
    };

    store.updateDeviceState({
      busy: state.processingId !== 0n,
      progress: state.progressPct,
      electrodeDetected: state.electrode !== ElectrodeDetect.NC,
      electrodeType: electrodeType(),
      temperature: state.temperatureC,
      humidity: state.humidityPct,
      batteryLevel: state.batteryPct,
      batteryVoltage: state.batteryV,
      powerState: powerState(),
      warnings: Number(state.issueBitmask),
    });
  }

  /**
   * Handle USB plug-in event — attempt a silent reconnect if the plugged device
   * is our previously-paired Ediphor device and no reconnect is already running.
   *
   * The `connect` event fires a few milliseconds after the OS enumerates the
   * device, but device.open() can still fail while the OS finishes driver setup.
   * We wait a short settle delay before calling tryAutoReconnect() to avoid
   * transient SecurityError / NetworkError failures.
   */
  private handleConnect = (event: USBConnectionEvent): void => {
    if (this._isPageUnloading) return;
    if (this.device) return; // already connected
    if (!localStorage.getItem(DEVICE_STORAGE.PAIRED_KEY)) return; // never paired

    // Only react to our device (vendor + any of our product IDs)
    const d = event.device;
    if (
      d.vendorId !== this.options.vendorId ||
      !this.options.productIds.includes(d.productId)
    ) {
      return;
    }

    this.log('USB connect event — Ediphor device plugged in, scheduling auto-reconnect');
    logger.debug('[DeviceService] USB connect: scheduling auto-reconnect after OS settle delay');

    // Small delay so the OS finishes driver enumeration before we try open().
    setTimeout(() => {
      if (this._isPageUnloading || this.device) return;
      this.tryAutoReconnect().then((success) => {
        if (!success) {
          logger.debug('[DeviceService] USB connect: auto-reconnect failed (device may still be initialising)');
        } else {
          logger.debug('[DeviceService] USB connect: auto-reconnect succeeded ✓');
        }
      }).catch((err) => {
        logger.warn('[DeviceService] USB connect: auto-reconnect threw', err);
      });
    }, 500);
  };

  /**
   * Handle device disconnect event
   */
  private handleDisconnect = (event: USBConnectionEvent): void => {
    if (event.device === this.device) {
      // The browser fires USB disconnect events as part of page teardown.
      // Skip all store mutations and reconnect scheduling — the page is going
      // away anyway, and re-renders at this point would flash error dialogs.
      if (this._isPageUnloading) {
        this.log('handleDisconnect: page unloading — skipping state update');
        return;
      }

      this.log('Device disconnected');
      this.device = null;
      this._transportOverride = null;
      this._stopReceiveLoop();

      const store = useDeviceStore.getState();
      store.setConnectionState('disconnected');
      store.setError(createDeviceError(DeviceErrorCode.CONNECTION_LOST));

      // NOTE: Background auto-reconnect is intentionally NOT scheduled here.
      // Each view that cares about reconnection (DeviceReconnectDialog during
      // incubation, MeasurementInterruptedModal during specimen measurement,
      // connection wizard on the home page) calls tryAutoReconnect() explicitly.
      // Scheduling a background attempt here races with those calls because
      // tryAutoReconnect() uses a single _autoReconnectInProgress guard, so
      // the second caller immediately gets false and shows an incorrect error.
    }
  };

  /**
   * Estimate remaining time based on progress
   */
  private estimateTimeRemaining(progress: number, startTime: number): number {
    if (progress <= 0) return 0;
    const elapsed = Date.now() - startTime;
    const total = elapsed / (progress / 100);
    return Math.max(0, total - elapsed);
  }

  /**
   * Debug logging
   */
  private log(...args: unknown[]): void {
    const message = args
      .map((arg) => (typeof arg === 'object' ? JSON.stringify(arg, null, 2) : String(arg)))
      .join(' ');
    logger.debug('[DeviceService]', message);
  }

  private _onBeforeUnload = (): void => {
    this._isPageUnloading = true;
  };
}

// Default export for convenience
export const deviceService = DeviceService.getInstance();
